/***********************/
/* Angular + external */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { Subscription } from "rxjs";
/***********************/
/* BFX */
// services

// models

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { UserLookupService } from "shared/cubiq-services";
import { runDetached } from "../../../shared/helper-services/run-detached";
import { OENCommand } from "../data/enums/oe-command.enum";
import { OENDisplayState } from "../data/enums/oe-display-state.enum";
import { OENEntryType } from "../data/enums/oe-entry-type.enum";
import { OENInputControl } from "../data/enums/oe-input-control.enum";
import { OENLockedState } from "../data/enums/oe-locked-state.enum";
import { OENOperationMode } from "../data/enums/oe-operation-mode.enum";
import { OECommandEventModel } from "../data/models/oe-command-event.model";
import { OELockingProfileTransportModel } from "../data/models/oe-locking-profile.model";
import { OELoggerService } from "../services/basic/oe-logger.service";
import { OEApiService } from "../services/oe-api.service";
import { OECoordinatorService } from "../services/oe-coordinator.service";
import { IActionCallout } from "app/bfx-element-library/models/CalloutModel";

const MONITOR_EXECUTION_TIMEOUT = 10000;

@Component({
  selector: "bfx-panel-notifications",
  templateUrl: "./html/bfx-panel-notifications.html",
  standalone: false,
})
export class OENotificationsPanelComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input() refId: string;
  @Input() rootRefId: string;
  @Input() entryType: OENEntryType;
  @Input() operationMode: OENOperationMode;

  // Hoist enum for template
  public OENDisplayState = OENDisplayState;

  /***************************************/
  /* DATA */
  public data = {
    message: "Test",
    tooltip: "",
    notification_config: OENDisplayState.SHOW_ORDER_ENTRY,
    button_locks: {
      oco: false, // actual locking variable
      ocoLockState: OENLockedState.UNLOCKED, // locking state kept in the panel
      ifDone: false,
      ifDoneLockState: OENLockedState.UNLOCKED,
      loop: false,
      loopLockState: OENLockedState.UNLOCKED,
      timeout: false,
      timeoutLockState: OENLockedState.UNLOCKED,
    },
    button_labels: {
      oco: "OCO",
      ifDone: "If Done",
      loop: "Loop",
      timeout: "Timeout",
    },
    warning: {
      show: false,
      message: "You do not have sufficient privileges to save or submit the order",
    },
    monitor_execution: {
      // Used when MONITOR_EXECUTION callouts are enabled
      callout_type: "positive",
      callout_size: "sm",
      callout_message: null,
    },
  };

  private lockAllFg: boolean = false;
  private tempLockAllFg: boolean = false;

  private timeoutId: any;

  //private configurationSubscription: Subscription;
  private coordinatorSubscription: Subscription;

  /**
   * Class used for applying visibility animations - any div using this class will be
   * subject to the scheduling timers (schedule())
   */
  public visibilityClass: string = "callout-hidden";

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    protected oeLogger: OELoggerService,
    private coordinator: OECoordinatorService,
    private api: OEApiService,
    protected translate: TranslateService,
    protected userService: UserLookupService,
    public cd: ChangeDetectorRef,
    private element: ElementRef,
  ) {
    this.initialize();
    this.oeLogger = oeLogger.for(__dirname);
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.coordinatorSubscription.unsubscribe();
    //this.configurationSubscription.unsubscribe();
  }

  ngAfterViewInit() {
    this.initializePermissions();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initialize(): void {
    this.subscribeToCoordinator();
  }

  private initializePermissions(): void {
    if (typeof this.rootRefId === "string" && this.rootRefId.length > 1) {
      this.data.warning.show = !this.api.getPermissionEnterOrder(this.rootRefId);
      this.data.warning.message = this.translate.instant(
        "order_entry.permission.enterNewOrderWarning",
      );
    } else {
      logger.out.error(
        `ERROR: _oePanelNotifications.initializePermissions(): Unknown refId: '${this.rootRefId}'`,
      );
    }
  }

  private subscribeToCoordinator(): void {
    this.coordinatorSubscription = this.coordinator.notificationsPanelCmd$
      .pipe(runDetached(this))
      .subscribe(($event: OECommandEventModel) => {
        if ($event.ref === this.refId || $event.ref === this.rootRefId) {
          this.oeLogger.outputDevLogToConsole(
            `DEV: NotificationsPanel -> UPDATE: ${OENCommand[$event.cmd]} | Event: `,
            false,
            $event,
          );

          switch ($event.cmd) {
            case OENCommand.LOCK_ALL:
              this.lockAll();
              break;
            case OENCommand.UNLOCK_ALL:
              this.unlockAll();
              break;
            case OENCommand.LOAD_LOCKING_PROFILE:
              this.loadLockProfile($event.data);
              break;
            case OENCommand.RESET_ALL:
              this.resetAll();
              break;
            case OENCommand.STATE_ORDER_ENTRY:
            case OENCommand.STATE_ORDER_IN_MODIFICATION:
            case OENCommand.STATE_ORDER_AMEND_MARKUP:
              this.data.notification_config = OENDisplayState.SHOW_ORDER_ENTRY;
              break;
            case OENCommand.STATE_ORDER_PROCESSING:
              this.data.tooltip = "";
              this.data.message = "Processing..";
              this.data.notification_config = OENDisplayState.SHOW_PROCESSING;
              break;
            case OENCommand.STATE_MONITOR_EXECUTION:
              this.data.warning.show = false;
              this.data.tooltip = null;
              this.data.notification_config = OENDisplayState.SHOW_MONITOR_EXECUTION;
              break;
            case OENCommand.STATE_ORDER_SUCCESS:
              this.data.tooltip = "";
              this.data.message =
                $event.data != null && typeof $event.data === "string"
                  ? $event.data
                  : $event.data != null &&
                      $event.data.message != null &&
                      typeof $event.data.message === "string"
                    ? $event.data.message
                    : "Active";
              this.data.notification_config = OENDisplayState.SHOW_SUCCESS;
              break;
            case OENCommand.STATE_ORDER_REJECTED:
            case OENCommand.STATE_ORDER_REJECTED_HCO_ERROR:
              // normalize data if object (structure change for OMS-33530)
              $event.data =
                $event.data != null && typeof $event.data.error === "string"
                  ? $event.data.error
                  : $event.data;
              //
              this.data.message =
                $event.data != null && typeof $event.data === "string" ? $event.data : "Rejected";
              this.data.tooltip = (this.data.message as string).replaceAll(/<br\/>/gi, "\n");
              this.data.notification_config = OENDisplayState.SHOW_REJECTED;
              break;
            case OENCommand.STATE_ORDER_APPROVAL_ALLOWED:
              this.data.message = "Dealer Intervention Needed!";
              this.data.tooltip =
                $event.data != null && typeof $event.data.tooltip === "string"
                  ? $event.data.tooltip
                  : "";
              this.data.notification_config = OENDisplayState.SHOW_APPROVAL_ALLOWED;
              break;
            case OENCommand.STATE_ORDER_APPROVAL_INTERNAL:
              this.data.message = "Dealer Intervention Needed!";
              this.data.tooltip =
                $event.data != null && typeof $event.data.tooltip === "string"
                  ? $event.data.tooltip
                  : "";
              this.data.notification_config = OENDisplayState.SHOW_APPROVAL_INTERNAL;
              break;
            case OENCommand.STATE_ORDER_APPROVAL_EXTERNAL:
              this.data.message = "Dealer Intervention Needed!";
              this.data.tooltip =
                $event.data != null && typeof $event.data.tooltip === "string"
                  ? $event.data.tooltip
                  : "";
              this.data.notification_config = OENDisplayState.SHOW_APPROVAL_EXTERNAL;
              break;
            case OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE:
              this.data.message =
                $event.data != null && typeof $event.data.msg === "string"
                  ? $event.data.msg
                  : "Modify Out Acceptance Needed";
              this.data.tooltip =
                $event.data != null && typeof $event.data.tooltip === "string"
                  ? $event.data.tooltip
                  : "";
              this.data.notification_config = OENDisplayState.SHOW_MODIFY_OUT_ACCEPTANCE;
              break;
            case OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE:
              this.data.message =
                $event.data != null && typeof $event.data.msg === "string"
                  ? $event.data.msg
                  : "External Modify Out Acceptance Needed";
              this.data.tooltip =
                $event.data != null && typeof $event.data.tooltip === "string"
                  ? $event.data.tooltip
                  : "";
              this.data.notification_config = OENDisplayState.SHOW_APPROVAL_EXTERNAL_MODIFY;
              break;
            case OENCommand.STATE_ORDER_ACCEPTANCE_ALLOWED:
              this.data.message =
                $event.data != null && typeof $event.data.msg === "string"
                  ? $event.data.msg
                  : "Acceptance Needed: " + " <missing desk>";
              this.data.tooltip =
                $event.data != null && typeof $event.data.tooltip === "string"
                  ? $event.data.tooltip
                  : "";
              this.data.notification_config = OENDisplayState.SHOW_ACCEPTANCE_ALLOWED;
              break;
            case OENCommand.STATE_ORDER_ACCEPTANCE_CALL:
              this.data.message =
                $event.data != null && typeof $event.data.msg === "string"
                  ? $event.data.msg
                  : "Acceptance Needed: " + " <missing desk>";
              this.data.tooltip =
                $event.data != null && typeof $event.data.tooltip === "string"
                  ? $event.data.tooltip
                  : "";
              this.data.notification_config = OENDisplayState.SHOW_ACCEPTANCE_CALL;
              break;
            case OENCommand.STATE_ORDER_ACCEPTANCE_EXTERNAL:
              this.data.message =
                $event.data != null && typeof $event.data.msg === "string"
                  ? $event.data.msg
                  : "External Acceptance Needed: " + " <missing desk>";
              this.data.tooltip =
                $event.data != null && typeof $event.data.tooltip === "string"
                  ? $event.data.tooltip
                  : "";
              this.data.notification_config = OENDisplayState.SHOW_ACCEPTANCE_EXTERNAL;
              break;
            case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_ERROR:
              this.data.tooltip = "";
              this.data.message = $event.data;
              // TODO Add separate state, or keep same for style matching
              this.data.notification_config = OENDisplayState.SHOW_REJECTED;
              break;
            case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_WARN:
              this.data.tooltip = "";
              this.data.message = $event.data;
              // TODO Add separate state, or keep same for style matching
              this.data.notification_config = OENDisplayState.SHOW_PROCESSING;
              break;
            case OENCommand.STATE_ORDER_INT_ERROR:
              this.data.message = $event.data;
              this.data.tooltip = "";
              this.data.notification_config = OENDisplayState.SHOW_REJECTED;
              break;
            case OENCommand.STATE_ORDER_UNLOCKED:
              if ($event.data != null && typeof $event.data === "string") {
                this.data.message = $event.data;
              } else if (
                $event.data != null &&
                $event.data.message != null &&
                typeof $event.data.message === "string"
              ) {
                this.data.message = $event.data.message;
              } else if (!this.api.isOperationModeMonitorExecution(this.refId)) {
                this.data.message = "Rejected: User Aborted Operation ";
              }
              this.data.tooltip = "";
              this.data.notification_config = OENDisplayState.SHOW_UNLOCKED;
              break;
            case OENCommand.STATE_ORDER_LOST_HIDDEN:
              this.data.tooltip = "";
              this.data.message = this.translate.instant("execution_monitor.visibility_lost");
              this.data.notification_config = OENDisplayState.SHOW_LOST_HIDDEN;
              break;
            case OENCommand.STATE_ORDER_EXPIRY_PENDING:
              if ($event.data != null && typeof $event.data === "string") {
                this.data.message = $event.data;
              } else if (
                $event.data != null &&
                $event.data.message != null &&
                typeof $event.data.message === "string"
              ) {
                this.data.message = $event.data.message;
              } else {
                // error handling if no message
                this.data.message = "Expiry Pending";
              }
              this.data.tooltip = "";
              this.data.notification_config = OENDisplayState.SHOW_EXPIRY_PENDING;
              break;
            case OENCommand.UNBULK:
            case OENCommand.BULKED:
            case OENCommand.BULKING:
              break;
            case OENCommand.STATE_ORDER_MODIFICATION_EXTERNAL:
              this.data.notification_config = OENDisplayState.SHOW_MONITOR_EXECUTION;
              break;
            case OENCommand.EXECUTION_NOTIFICATION:
              this.processNewCallout($event.data as IActionCallout);
              break;
            case OENCommand.STATE_ORDER_ENTRY_NEGATIVE_MARKUP:
              this.data.notification_config = OENDisplayState.SHOW_NEGATIVE_MARKUP_WARN;
              this.data.tooltip = "";
              this.data.message = this.translate.instant(
                "order.entry.negative.markup.warning",
                $event?.data?.message ? [$event.data.message] : [""],
              );
              break;
            case OENCommand.BM_ZERO_AMOUNT_WARN:
              this.data.notification_config = OENDisplayState.SHOW_BM_ZERO_AMOUNT_WARN;
              const errorId = $event?.data?.isAllocation
                ? "order_entry.dialog.bm_zero_allocation.message"
                : "order_entry.dialog.bm_zero.message";
              this.data.message = this.translate.instant(errorId);
              break;
            default:
              logger.out.error(
                `opn-c - ERROR: oeNotificationsPanel.subscribeToCoordinator(): Command not implemented! command= (${
                  $event.cmd
                }) [${$event.cmd != null ? OENCommand[$event.cmd] : "NULL"}]`,
              );
              break;
          }
        }
      });
  }

  /**********************************************************************/
  /* PRIVATE METHODS */

  protected resetAll(): void {
    this.data.button_locks.ocoLockState = OENLockedState.UNLOCKED;
    this.data.button_locks.ifDoneLockState = OENLockedState.UNLOCKED;
    this.data.button_locks.loopLockState = OENLockedState.UNLOCKED;
    this.data.button_locks.timeoutLockState = OENLockedState.UNLOCKED;
    this.updateLocking();
  }

  protected lockAll(): void {
    this.lockAllFg = true;
    this.updateLocking();
  }

  protected unlockAll(): void {
    this.lockAllFg = false;
    this.updateLocking();
  }

  protected loadLockProfile(lockingProfile: OELockingProfileTransportModel): void {
    if (
      lockingProfile instanceof OELockingProfileTransportModel &&
      Array.isArray(lockingProfile.profile) &&
      lockingProfile.profile.length > 0
    ) {
      for (let lockStateItem of lockingProfile.profile) {
        switch (lockStateItem.input) {
          case OENInputControl.BTN_OCO:
            this.data.button_locks.ocoLockState = lockStateItem.lockState;
            break;
          case OENInputControl.BTN_IF_DONE:
            this.data.button_locks.ifDoneLockState = lockStateItem.lockState;
            break;
          case OENInputControl.BTN_LOOP:
            this.data.button_locks.loopLockState = lockStateItem.lockState;
            break;
          case OENInputControl.BTN_TIMEOUT:
            this.data.button_locks.timeoutLockState = lockStateItem.lockState;
            break;
          default:
            logger.out.error(
              "opn-c -ERROR: _oePanelNotifications.handleLoadLockProfile(): unidentified lock item: ",
              lockStateItem,
            );
            break;
        }
      }
      this.updateLocking();
    }
  }

  /**
   * This is the only method where the locking state (e.g. this.data.button_locks.oco) is directly manipulated, the locking should be
   * managed through the locking state (e.g. this.data.button_locks.ocoLockState) in other methods.
   */
  private updateLocking(): void {
    if (!this.lockAllFg && !this.tempLockAllFg) {
      // global lock is UNLOCKED
      // BTN_OCO:
      if (this.data.button_locks.ocoLockState === OENLockedState.LOCKED) {
        this.data.button_locks.oco = true;
      } else if (this.data.button_locks.ocoLockState === OENLockedState.UNLOCKED) {
        this.data.button_locks.oco = false;
      } else {
        this.data.button_locks.oco = false;
        logger.out.error(`opn-c - Invalid NotificationsPanel->OCO lock state!`); // remove after dev testing
      }
      // BTN_IF_DONE:
      if (this.data.button_locks.ifDoneLockState === OENLockedState.LOCKED) {
        this.data.button_locks.ifDone = true;
      } else if (this.data.button_locks.ifDoneLockState === OENLockedState.UNLOCKED) {
        this.data.button_locks.ifDone = false;
      } else {
        this.data.button_locks.ifDone = false;
        logger.out.error(`opn-c - Invalid NotificationsPanel->IfDone lock state!`); // remove after dev testing
      }
      // BTN_LOOP:
      if (this.data.button_locks.loopLockState === OENLockedState.LOCKED) {
        this.data.button_locks.loop = true;
      } else if (this.data.button_locks.loopLockState === OENLockedState.UNLOCKED) {
        this.data.button_locks.loop = false;
      } else {
        this.data.button_locks.loop = false;
        logger.out.error(`opn-c - Invalid NotificationsPanel->LOOP lock state!`); // remove after dev testing
      }
      // BTN_TIMEOUT:
      if (this.data.button_locks.timeoutLockState === OENLockedState.LOCKED) {
        this.data.button_locks.timeout = true;
      } else if (this.data.button_locks.timeoutLockState === OENLockedState.UNLOCKED) {
        this.data.button_locks.timeout = false;
      } else {
        this.data.button_locks.timeout = false;
        logger.out.error(`opn-c - Invalid NotificationsPanel->Timeout lock state!`); // remove after dev testing
      }
    } else {
      // global lock is LOCKED
      this.data.button_locks.oco = true;
      this.data.button_locks.loop = true;
      this.data.button_locks.ifDone = true;
      this.data.button_locks.timeout = true;
    }
  }

  private executeLockUnlock(): void {
    if (!this.lockAllFg && !this.tempLockAllFg) {
      this.tempLockAllFg = true;
      this.updateLocking();
      setTimeout(() => {
        this.tempLockAllFg = false;
        this.updateLocking();
      }, 1000);
    }
  }

  /**********************************************************************/
  /* PUBLIC METHODS */

  public addIfDone() {
    this.executeLockUnlock();
    this.api.actionCreateIfDone(this.refId);
  }

  public addLoop() {
    this.executeLockUnlock();
    this.api.actionCreateLoop(this.refId);
  }

  public addOco() {
    this.executeLockUnlock();
    this.api.actionCreateOco(this.refId);
  }

  public addTimeout() {
    this.executeLockUnlock();
    this.api.actionCreateTimeout(this.refId);
  }

  private processNewCallout(calloutAction: IActionCallout) {
    const hasCallout: boolean = calloutAction?.calloutMessage?.length > 0;
    if (hasCallout) {
      logger.out.info(`opn-c - Showing new callout ${calloutAction.calloutMessage}`);
    } else {
      logger.out.info(`opn-c - clearing callout`);
    }

    // Update here and they'll be shown if in monitor execution state
    this.data.monitor_execution.callout_type = calloutAction?.calloutType;
    this.data.monitor_execution.callout_message = calloutAction?.calloutMessage;

    const calloutVisible = calloutAction?.calloutMessage?.length > 0;
    this.visibilityClass = calloutVisible ? "callout-visible" : "callout-hidden";
  }

  public hideCallout() {
    this.data.monitor_execution.callout_message = null;
    this.visibilityClass = "callout-hidden";
  }
  // /**
  //  * Hide the notifications that use visibilityClass after the specified interval
  //  * @param interval
  //  */
  // private schedule(interval) {
  //   // Always clear any pending transition
  //   if (this.timeoutId) {
  //     clearTimeout(this.timeoutId);
  //     this.timeoutId = 0;
  //   }

  //   const elements = (this.element.nativeElement as Element).getElementsByClassName(
  //     this.visibilityClass,
  //   );
  //   for (let element of elements) {
  //     element["style"].height = null;
  //   }
  //   this.visibilityClass = "callout-visible";

  //   if (interval > 0) {
  //     // First we make sure message is visible
  //     // for (let element of elements) {
  //     //   element["style"].height = "0px";
  //     // }
  //     // Can only animate height if height is explicitly specified - so first we wait until the specified display time
  //     // then calculate the actual height.
  //     // We then set the height explicitly as a style and 100ms later set the height to 0 which will animate the
  //     // height from full height to 0
  //     this.timeoutId = setTimeout(() => {
  //       // After first timer, explicitly set the height
  //       const elements = (this.element.nativeElement as Element).getElementsByClassName(
  //         this.visibilityClass,
  //       );
  //       for (let element of elements) {
  //         const rect = element.getBoundingClientRect();
  //         element["style"].height = rect.height + "px";
  //       }
  //       this.timeoutId = setTimeout(() => {
  //         // Immediately after, set height to 0 which should animate
  //         const elements = (this.element.nativeElement as Element).getElementsByClassName(
  //           this.visibilityClass,
  //         );
  //         for (let element of elements) {
  //           element["style"].height = "0px";
  //         }
  //         this.visibilityClass = "callout-hiding";
  //         this.timeoutId = setTimeout(() => {
  //           // After animate duration change the style to hidden
  //           const elements = (this.element.nativeElement as Element).getElementsByClassName(
  //             this.visibilityClass,
  //           );
  //           for (let element of elements) {
  //             element["style"].height = null;
  //           }
  //           this.visibilityClass = "callout-hidden";
  //           this.timeoutId = 0;
  //           // Dispatch clear event at this stage as it should be considered to be hidden
  //           // this.onCalloutHide.next();
  //           this.cd.markForCheck();
  //         }, 1000);
  //         this.cd.markForCheck();
  //       }, 100);
  //     }, interval);
  //   }
  //   this.cd.markForCheck();
  // }
}
